// Data-mapping helpers + the two direct API calls that don't fit the saga model
// (attachment upload needs onUploadProgress; transcript is a binary download).
// Everything else flows through the redux saga (support_saga.js).
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';
import { getCookie } from '../../../util_functions';
import { get_api_url } from '../../../utils/urls';

const authHeaders = () => ({ Authorization: `Bearer ${getCookie('cms_token_3')}` });

// Sanitize rich-text HTML before render/send (keeps target/rel on links).
export const cleanHtml = (html) => DOMPurify.sanitize(html || '', { ADD_ATTR: ['target', 'rel'] });

// Derive a plain-text body from rich HTML (block tags → newlines, then read
// textContent).
export const htmlToPlainText = (html) => {
  const div = document.createElement('div');
  div.innerHTML = (html || '').replace(/<\/p>/g, '</p>\n').replace(/<br\s*\/?>/g, '\n');
  return (div.textContent || '').replace(/\n+$/, '').trim();
};

// Small dropdown/popup helper: open state + a ref that closes on outside-click
// or Escape.
export const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
};

// crm_reminder_department rows are serialised by GORM without json tags, so keys
// arrive PascalCase (UUID/Name). Tolerate both shapes.
export const teamUuid = (team) => team.uuid || team.UUID || '';
export const teamName = (team) => team.name || team.Name || '';

// Left-nav team options. No emoji/colour comes from the backend, so the emoji
// column the wireframe used is dropped (kept as '' for layout compatibility).
export const mapTeams = (teams = []) =>
  teams.map((t) => ({ key: teamUuid(t), label: teamName(t), emoji: '' }));

// Assignable agents → picker options. `you` is resolved by matching the logged-in
// CMS user's email (bootstrap agents don't carry a "you" flag).
export const mapAgents = (agents = [], myEmail = '') =>
  agents.map((a) => ({
    key: a.uuid,
    label: a.name,
    email: a.email,
    department_uuid: a.department_uuid,
    you: !!myEmail && a.email && a.email.toLowerCase() === myEmail.toLowerCase()
  }));

// Tag pool → list of names for the tag picker.
export const mapTagPool = (tags = []) => tags.map((t) => t.name);

// Resolve a conversation contact to its CMS profile link. The GET conversation
// payload's `contact` carries {type, student, teacher}; an older payload had
// only `student`, so a missing type falls back to student-if-present else guest.
// Guests (and linked people missing a uuid) are not linkable — href is null.
// Routes match the app router: /studentprofile?id= and /teacherprofile?id=.
export const contactProfile = (contact) => {
  const c = contact || {};
  const type = c.type || (c.student ? 'student' : 'guest');
  let person = null;
  if (type === 'student') person = c.student;
  else if (type === 'teacher') person = c.teacher;
  const uuid = person && person.uuid;
  if (uuid && (type === 'student' || type === 'teacher')) {
    const base = type === 'student' ? '/studentprofile' : '/teacherprofile';
    return { type, href: `${base}?id=${encodeURIComponent(uuid)}` };
  }
  return { type: type === 'student' || type === 'teacher' ? type : 'guest', href: null };
};

// Our backend's default profile image, used as the single fallback when a
// person carries no real photo or the photo URL fails to load. The backend now
// always returns a non-empty `avatar` (real photo or this default), so this is
// only a defensive fallback for missing fields / broken image loads.
export const DEFAULT_AVATAR =
  'https://cloudimages.myyogateacher.com/dgerdfai4/image/upload/v1587531490/ic_default_profile.png';

// Real profile photo for a thread-detail contact ({type, student, teacher}).
// Picks the avatar of whichever person matches the contact type; falls back to
// the default image when absent. Tolerates a missing `type` (student-if-present).
export const contactAvatar = (contact) => {
  const c = contact || {};
  const type = c.type || (c.student ? 'student' : 'guest');
  const person = type === 'teacher' ? c.teacher : c.student;
  return (person && person.avatar) || DEFAULT_AVATAR;
};

// Short relative time ("3h", "2d") for list rows / timeline stamps.
export const shortAgo = (ts) => {
  if (!ts) return '';
  const m = moment(ts);
  if (!m.isValid()) return '';
  return m.fromNow(true);
};

// Absolute stamp for message bubbles.
export const stamp = (ts) => {
  if (!ts) return '';
  const m = moment(ts);
  return m.isValid() ? m.format('D MMM, hh:mm A') : '';
};

// UI operator label ↔ backend token. The compiler accepts both space- and
// underscore-separated tokens, but we send the canonical underscore form.
export const OPERATOR_TOKEN = {
  equals: 'equals',
  'not equals': 'not_equals',
  set: 'set',
  'not set': 'not_set',
  'greater than': 'greater_than',
  'less than': 'less_than',
  between: 'between'
};

// Humanise a timeline event into the grey one-liner the thread renders.
export const humanizeEvent = (event, agentsByUuid = {}) => {
  let { payload } = event;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      payload = {};
    }
  }
  payload = payload || {};
  const actor = event.actor_cms_uuid && agentsByUuid[event.actor_cms_uuid];
  const who = actor ? actor.label : 'System';
  switch (event.type) {
    case 'status_changed':
      return `${who} changed status to ${payload.to || ''}`;
    case 'priority_changed':
      return `${who} set priority to ${payload.to || 'none'}`;
    case 'assigned_agent': {
      const to = payload.to && agentsByUuid[payload.to];
      return to ? `${who} assigned to ${to.label}` : `${who} unassigned the conversation`;
    }
    case 'assigned_team':
      return payload.to ? `${who} assigned to a team` : `${who} removed the team`;
    case 'tag_added':
      return `${who} added tag ${payload.tag || ''}`;
    case 'tag_removed':
      return `${who} removed tag ${payload.tag || ''}`;
    case 'snoozed':
      return `${who} snoozed the conversation`;
    case 'reopened':
      return `${who} reopened the conversation`;
    case 'conversation_created':
      return 'Conversation created';
    case 'auto_ack_sent':
      return 'Auto-acknowledgement sent to the customer';
    // Outbound delivery lifecycle + engagement (Resend webhooks). These are
    // System events with no actor — render a plain human label so the timeline
    // never shows the raw `System · email_opened` type string.
    case 'email_opened':
      return 'Customer opened the email';
    case 'email_clicked':
      return 'Customer clicked a link in the email';
    case 'delivery_bounced':
      return 'Email bounced';
    case 'delivery_complained':
      return 'Customer marked the email as spam';
    case 'delivery_failed':
      return 'Email delivery failed';
    case 'delivery_delayed':
      return 'Email delivery delayed';
    case 'email_suppressed':
      return 'Email suppressed (recipient on suppression list)';
    default:
      return `${who} · ${event.type}`;
  }
};

// Build the explicit mentions array (uuid + name) the notes API expects, by
// matching bootstrap agent display names present as "@Name" in the note body.
export const extractMentions = (body = '', agents = []) => {
  const out = [];
  agents.forEach((a) => {
    if (a.label && body.includes(`@${a.label}`)) {
      out.push({ cms_user_uuid: a.key, name: a.label });
    }
  });
  return out;
};

// POST /v2/support/attachments (multipart 'file'). onProgress(0..100).
// People search backed by chat_server's mod_user_external `/search` endpoint.
// `role` is 'student' | 'teacher'. Returns [{ uuid, name, email, avatar }].
export const searchUsers = (filter, role) => {
  const url = get_api_url('v1.0/users/search', false, false, true);
  return axios({
    method: 'post',
    url,
    headers: authHeaders(),
    data: { filter, role }
  }).then((res) => {
    const users = (res.data && res.data.users) || [];
    return users.map((u) => ({
      uuid: u.uuid,
      name: [u.fname, u.lname].filter(Boolean).join(' ').trim(),
      email: u.email || '',
      avatar: u.avatar || ''
    }));
  });
};

export const uploadAttachment = (file, onProgress) => {
  const url = get_api_url('/v2/support/attachments', false, true);
  const form = new FormData();
  form.append('file', file);
  return axios({
    method: 'post',
    url,
    headers: { ...authHeaders(), 'content-type': 'multipart/form-data' },
    data: form,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    }
  }).then((res) => res.data.data);
};

// GET /v2/support/conversations/:uuid/transcript?format= → triggers a download.
export const downloadTranscript = (uuid, ticketNumber, format = 'txt') => {
  const url = get_api_url(
    `/v2/support/conversations/${uuid}/transcript?format=${format}`,
    false,
    true
  );
  return axios({
    method: 'get',
    url,
    headers: authHeaders(),
    responseType: 'blob'
  }).then((res) => {
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `transcript-${ticketNumber || uuid}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  });
};
